/**
 * createAuthStore — one factory for the three auth sessions (tenant, customer,
 * platform). Each store owns its localStorage keys, a `load()` that hydrates
 * `user` from `/me`, and `setSession` / `clearSession`.
 */
import { create } from "zustand";

export function createAuthStore({ tokenKey, refreshKey, userKey, clearKeys = [], fetchMe, shouldLoad = () => true, onBeforeLoad, onLoadError = "keep", extend }) {
  const keys = [tokenKey, refreshKey, userKey, ...clearKeys].filter(Boolean);
  return create((set, get) => ({
    user: null,
    loading: true,

    load: async () => {
      onBeforeLoad?.();
      const token = localStorage.getItem(tokenKey);
      if (!token || !shouldLoad()) { set({ loading: false }); return; }
      try {
        const res = await fetchMe();
        set({ user: res.data });
        if (userKey) localStorage.setItem(userKey, JSON.stringify(res.data));
      } catch (err) {
        const status = err?.response?.status;
        // Only a real auth rejection ends the session. 429 / 5xx / offline must not log people out:
        // wait for Retry-After (max 5 s), try again, then fall back to the cached profile.
        if (status !== 401 && status !== 403) {
          const wait = Math.min(5, Number(err?.response?.headers?.["retry-after"]) || 1.5) * 1000;
          await new Promise((r) => setTimeout(r, wait));
          try { const retry = await fetchMe(); set({ user: retry.data }); return; } catch {}
          const cached = userKey && localStorage.getItem(userKey);
          if (cached) { try { set({ user: JSON.parse(cached) }); } catch {} }
          return;
        }
        if (onLoadError === "retry" && localStorage.getItem(tokenKey)) {
          try { const retry = await fetchMe(); set({ user: retry.data }); return; } catch {}
        }
        if (onLoadError !== "keep") get().clearSession();
      } finally {
        set({ loading: false });
      }
    },

    setSession: (token, refreshToken, user) => {
      localStorage.setItem(tokenKey, token);
      if (refreshKey && refreshToken) localStorage.setItem(refreshKey, refreshToken);
      if (userKey && user) localStorage.setItem(userKey, JSON.stringify(user));
      set({ user });
    },

    clearSession: () => {
      keys.forEach(k => localStorage.removeItem(k));
      localStorage.removeItem("admin_selected_microsite_id");
      set({ user: null });
    },

    ...(extend ? extend(set, get) : {}),
  }));
}
